import React from 'react';
import './Modal.css';

interface BookingItem {
  id: string;
  resource_type: "DESK" | "MEETING_ROOM";
  resource_name: string;
  total_amount: string;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  item?: BookingItem;
  currency?: string;
  isLoading?: boolean;
}

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  item,
  currency,
  isLoading = false
}: ConfirmationModalProps) {
  if (!isOpen) return null;

  const getResourceIcon = (type: string) => {
    return type === 'DESK' ? '🪑' : '🏢';
  };

  return (
    <div className="modal-overlay" onClick={!isLoading ? onClose : undefined}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {!isLoading && (
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        )}
        
        <div className="modal-header">
          <div className={`modal-icon ${isLoading ? 'loading' : 'cancel-booking'}`}>
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              '🗑️'
            )}
          </div>
        </div>
        
        <div className="modal-content">
          <h2 className="modal-title">{isLoading ? 'Cancelling Booking' : title}</h2>
          
          {isLoading && (
            <p className="modal-description">
              Please wait while we process your cancellation...
            </p>
          )}
          
          {item && (
            <div className="modal-booking-details">
              <div className="modal-booking-detail-row">
                <div className="modal-booking-detail-left">
                  <span className="modal-resource-icon">{getResourceIcon(item.resource_type)}</span>
                  <div>
                    <p className="modal-booking-label">Resource</p>
                    <p className="modal-booking-value">{item.resource_name}</p>
                  </div>
                </div>
              </div>
              
              <div className="modal-booking-detail-row">
                <div>
                  <p className="modal-booking-label">Type</p>
                  <p className="modal-booking-value">{item.resource_type.replace('_', ' ')}</p>
                </div>
              </div>
              
              <div className="modal-booking-detail-row amount-row">
                <div>
                  <p className="modal-booking-label">Amount</p>
                  <p className="modal-booking-amount">{currency} {item.total_amount}</p>
                </div>
              </div>
              
              {!isLoading && (
                <p className="modal-disclaimer">
                  * Amount is non-refundable
                </p>
              )}
            </div>
          )}
        </div>
        
        {!isLoading && (
          <div className="modal-actions">
            <button className="modal-btn cancel" onClick={onClose}>
              Cancel
            </button>
            <button className="modal-btn confirm" onClick={onConfirm}>
              Confirm Cancellation
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
}

export function SuccessModal({
  isOpen,
  onClose,
  title,
  description
}: SuccessModalProps) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-icon success">
            ✓
          </div>
        </div>
        
        <div className="modal-content">
          <h2 className="modal-title success-title">{title}</h2>
          <p className="modal-description success-description">{description}</p>
        </div>
        
        <div className="modal-actions single">
          <button className="modal-btn success" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

interface LogoutConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export function LogoutConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false
}: LogoutConfirmationModalProps) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={!isLoading ? onClose : undefined}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        {!isLoading && (
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        )}
        
        <div className="modal-header">
          <div className={`modal-icon ${isLoading ? 'loading' : 'logout'}`}>
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              '👋'
            )}
          </div>
        </div>
        
        <div className="modal-content">
          <h2 className="modal-title">{isLoading ? 'Logging Out' : 'Confirm Logout'}</h2>
          
          {isLoading && (
            <p className="modal-description">
              Please wait while we log you out...
            </p>
          )}
          
          {!isLoading && (
            <p className="modal-description">
              Are you sure you want to logout from your account?
            </p>
          )}
        </div>
        
        {!isLoading && (
          <div className="modal-actions">
            <button className="modal-btn cancel" onClick={onClose}>
              Cancel
            </button>
            <button className="modal-btn logout" onClick={onConfirm}>
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
